import { useState, useCallback } from 'react'
import { getUploadUrl } from '@/api/userApi'
import axios from 'axios'

export interface URLImage {
  id?: string
  index?: number
  key: string
  url: string
}

export interface imageUploadOptions {
  linkFor:
    | 'eventImages'
    | 'eventGuestProfiles'
    | 'activityHighlights'
    | 'profile'
    | 'advertisementSponsorLogo'
    | 'advertisementEventImage'
  maxFiles?: number
  maxFileSize?: number
  allowedTypes?: string[]
  onSuccess?: (key: string, file: File, previewUrl: string) => void
  onError?: (error: string) => void
}

export interface UseImageUploadReturn {
  uploadFiles: (files: File[]) => Promise<string[] | undefined>
  isUploading: boolean
  uploadProgress: number
  getExistingImageFiles: (images: URLImage[]) => Promise<File[]>
}

export default function useImageUpload(
  options: imageUploadOptions
): UseImageUploadReturn {
  const {
    linkFor,
    maxFiles = 10,
    maxFileSize = 5 * 1024 * 1024, // 5MB
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'],
    onSuccess,
    onError,
  } = options

  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!allowedTypes.includes(file.type)) {
        const typesString = allowedTypes
          .map(type => type.split('/')[1].toUpperCase())
          .join(', ')
        return `Please select a ${typesString} file`
      }

      if (file.size > maxFileSize) {
        const sizeMB = Math.round(maxFileSize / (1024 * 1024))
        return `File size must be less than ${sizeMB}MB`
      }

      return null
    },
    [allowedTypes, maxFileSize]
  )

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const validationErrors: string[] = []
      const validFiles: File[] = []

      files.forEach((file, index) => {
        const error = validateFile(file)
        if (error) {
          validationErrors.push(`File ${index + 1}: ${error}`)
        } else {
          validFiles.push(file)
        }
      })

      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join(', ')
        onError?.(errorMessage)
        return
      }

      const filesToUpload = validFiles

      if (filesToUpload.length === 0) {
        const errorMessage = `Maximum ${maxFiles} files allowed`
        onError?.(errorMessage)
        return
      }

      setIsUploading(true)
      setUploadProgress(0)

      try {
        const presignPayloadData = filesToUpload.map(file => ({
          linkFor,
          fileType: file.type,
        }))

        // Get upload URLs
        const uploadUrlResponse = await getUploadUrl({
          presignPayload: presignPayloadData,
        })

        // Upload files with progress tracking
        const uploadPromises = filesToUpload.map(async (file, index) => {
          const uploadData = uploadUrlResponse[index]

          await axios.request({
            method: 'put',
            maxBodyLength: Infinity,
            url: uploadData.url,
            headers: {
              'Content-Type': file.type,
              'x-ms-blob-type': 'BlockBlob',
            },
            data: file,
            onUploadProgress: progressEvent => {
              if (progressEvent.total) {
                const fileProgress =
                  (progressEvent.loaded / progressEvent.total) * 100
                const overallProgress =
                  (index * 100 + fileProgress) / filesToUpload.length
                setUploadProgress(Math.round(overallProgress))
              }
            },
          })

          const previewUrl = URL.createObjectURL(file)
          onSuccess?.(uploadData.key, file, previewUrl)

          return uploadData.key
        })

        const keys = await Promise.all(uploadPromises)
        setUploadProgress(100)
        return keys
      } catch (error) {
        console.error('Image upload failed:', error)
        const errorMessage = 'Failed to upload images. Please try again.'
        onError?.(errorMessage)
        return []
      } finally {
        setIsUploading(false)
      }
    },
    [linkFor, maxFiles, validateFile, onSuccess, onError]
  )

  const getExistingImageFiles = async (images: URLImage[]) => {
    try {
      const files = await Promise.all(
        images.map(async image => {
          const response = await axios.get(image.url, {
            responseType: 'blob',
          })
          const blob = response.data

          const filename = `Existing image`
          return new File([blob], filename, { type: blob.type })
        })
      )

      return files
    } catch (error) {
      console.error('Failed to convert URLs to files:', error)
      const errorMessage = 'Failed to process existing images'
      onError?.(errorMessage)
      return []
    }
  }

  return {
    uploadFiles,
    isUploading,
    uploadProgress,
    getExistingImageFiles,
  }
}
